/**
 * Image filters as plain pixel maths. The previous version drew with `ctx.filter` (CSS filters on a
 * canvas), which Safari/iPhone does not support - so on an iPhone the download came out unfiltered.
 * Pure functions on RGBA arrays, same results on every browser, and testable without a DOM.
 */

export type Preset = "none" | "grayscale" | "sepia" | "invert" | "blur";

export interface FilterSettings {
  preset: Preset;
  /** 0-100: how strongly the preset is applied (Blur: strength in px at a 1000 px wide image). */
  intensity: number;
  /** percentages, 100 = unchanged */
  brightness: number;
  contrast: number;
  saturation: number;
}

export const DEFAULT_FILTER: FilterSettings = { preset: "none", intensity: 100, brightness: 100, contrast: 100, saturation: 100 };

const clamp = (v: number) => (v < 0 ? 0 : v > 255 ? 255 : v);

/** Box blur, three passes ~ gaussian, on RGBA data in place. radius in pixels. */
export function boxBlur(data: Uint8ClampedArray, w: number, h: number, radius: number): void {
  const r = Math.round(radius);
  if (r < 1) return;
  const tmp = new Uint8ClampedArray(data.length);
  const pass = (src: Uint8ClampedArray, dst: Uint8ClampedArray, horizontal: boolean) => {
    const len = horizontal ? w : h;
    const lines = horizontal ? h : w;
    const stepIn = horizontal ? 4 : w * 4;
    const stepLine = horizontal ? w * 4 : 4;
    const win = 2 * r + 1;
    for (let l = 0; l < lines; l++) {
      const base = l * stepLine;
      for (let c = 0; c < 4; c++) {
        let sum = 0;
        for (let k = -r; k <= r; k++) {
          const idx = Math.min(len - 1, Math.max(0, k));
          sum += src[base + idx * stepIn + c];
        }
        for (let i = 0; i < len; i++) {
          dst[base + i * stepIn + c] = sum / win;
          const out = Math.min(len - 1, Math.max(0, i - r));
          const inn = Math.min(len - 1, Math.max(0, i + r + 1));
          sum += src[base + inn * stepIn + c] - src[base + out * stepIn + c];
        }
      }
    }
  };
  for (let n = 0; n < 2; n++) {
    pass(data, tmp, true);
    pass(tmp, data, false);
  }
}

/** Apply the settings to RGBA pixels in place. `scale` = image width / 1000 (so blur looks the same at any size). */
export function applyFilterToPixels(data: Uint8ClampedArray, w: number, h: number, s: FilterSettings): void {
  const amt = Math.max(0, Math.min(1, s.intensity / 100));
  const b = s.brightness / 100;
  const k = s.contrast / 100;
  const sat = s.saturation / 100;

  if (s.preset === "blur") boxBlur(data, w, h, (s.intensity / 100) * 6 * (w / 1000) + 0);

  const doPreset = s.preset === "grayscale" || s.preset === "sepia" || s.preset === "invert";
  const needs = doPreset || b !== 1 || k !== 1 || sat !== 1;
  if (!needs) return;

  for (let i = 0; i < data.length; i += 4) {
    let r = data[i], g = data[i + 1], bl = data[i + 2];

    if (s.preset === "grayscale") {
      const y = 0.2126 * r + 0.7152 * g + 0.0722 * bl;
      r += (y - r) * amt; g += (y - g) * amt; bl += (y - bl) * amt;
    } else if (s.preset === "sepia") {
      const nr = 0.393 * r + 0.769 * g + 0.189 * bl;
      const ng = 0.349 * r + 0.686 * g + 0.168 * bl;
      const nb = 0.272 * r + 0.534 * g + 0.131 * bl;
      r += (nr - r) * amt; g += (ng - g) * amt; bl += (nb - bl) * amt;
    } else if (s.preset === "invert") {
      r += (255 - r - r) * amt; g += (255 - g - g) * amt; bl += (255 - bl - bl) * amt;
    }

    if (b !== 1) { r *= b; g *= b; bl *= b; }
    if (k !== 1) { r = (r - 127.5) * k + 127.5; g = (g - 127.5) * k + 127.5; bl = (bl - 127.5) * k + 127.5; }
    if (sat !== 1) {
      const y = 0.2126 * r + 0.7152 * g + 0.0722 * bl;
      r = y + (r - y) * sat; g = y + (g - y) * sat; bl = y + (bl - y) * sat;
    }
    data[i] = clamp(r); data[i + 1] = clamp(g); data[i + 2] = clamp(bl);
  }
}

export function applyFilterToCanvas(src: HTMLCanvasElement, s: FilterSettings, maxSide?: number): HTMLCanvasElement {
  const scale = maxSide ? Math.min(1, maxSide / Math.max(src.width, src.height)) : 1;
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(src.width * scale));
  c.height = Math.max(1, Math.round(src.height * scale));
  const ctx = c.getContext("2d")!;
  ctx.drawImage(src, 0, 0, c.width, c.height);
  const img = ctx.getImageData(0, 0, c.width, c.height);
  applyFilterToPixels(img.data, c.width, c.height, s);
  ctx.putImageData(img, 0, 0);
  return c;
}
